<template>
  <div class="cm-tool">
    <div class="cm-input-section">
      <div class="cm-input-label">
        <span class="cm-dot" />
        {{ $t('tool.ChartMaker.title') }} · {{ $t('tool.ChartMaker.subtitle') }}
      </div>

      <div class="cm-toolbar">
        <el-select v-model="chartType" size="medium" style="width:140px;" @change="renderChart">
          <el-option :label="$t('tool.ChartMaker.typeBar')" value="bar" />
          <el-option :label="$t('tool.ChartMaker.typeLine')" value="line" />
          <el-option :label="$t('tool.ChartMaker.typePie')" value="pie" />
          <el-option :label="$t('tool.ChartMaker.typeHBar')" value="hbar" />
        </el-select>
        <el-input v-model="title" size="medium" :placeholder="$t('tool.ChartMaker.phTitle')" style="flex:1;" @input="renderChart" />
        <el-button size="medium" icon="el-icon-plus" @click="addRow">{{ $t('tool.ChartMaker.addRow') }}</el-button>
        <el-button size="medium" plain icon="el-icon-star-off" @click="loadSample">{{ $t("common.action.sample") }}</el-button>
      </div>
    </div>

    <!-- Data table -->
    <div class="cm-table">
      <div class="cm-row cm-head">
        <span class="cm-cell cm-cell-swatch"></span>
        <span class="cm-cell cm-cell-label">{{ $t('tool.ChartMaker.colLabel') }}</span>
        <span class="cm-cell cm-cell-val">{{ $t('tool.ChartMaker.colValue') }}</span>
        <span class="cm-cell cm-cell-del"></span>
      </div>
      <div class="cm-row" v-for="(row, i) in rows" :key="i">
        <span class="cm-cell cm-cell-swatch">
          <span class="cm-swatch" :style="{ background: colors[i % colors.length] }" />
        </span>
        <input v-model="row.label" class="cm-cell cm-cell-label" :placeholder="$t('tool.ChartMaker.phLabel')" @input="renderChart" />
        <input v-model.number="row.value" type="number" class="cm-cell cm-cell-val" :placeholder="$t('tool.ChartMaker.phValue')" @input="renderChart" />
        <span class="cm-cell cm-cell-del" @click="removeRow(i)">&times;</span>
      </div>
      <div class="cm-row cm-summary" v-if="statsText">
        <span class="cm-sum-text">{{ statsText }}</span>
      </div>
    </div>

    <!-- Chart -->
    <div class="cm-chart" ref="chart" v-show="hasData"></div>

    <div class="cm-actions" v-if="hasData">
      <el-button size="small" plain icon="el-icon-download" @click="download">{{ $t('tool.ChartMaker.download') }}</el-button>
    </div>
  </div>
</template>

<script>
var echartsLoaded = false

export default {
  name:'ChartMakerTool',
  props:{ config:{ type:Object, default:function(){ return {} } } },
  data:function(){
    return {
      chartType:'bar', title:'',
      rows:[],
      chart:null,
      colors:['#4e7cff','#67c23a','#e6a23c','#f56c6c','#6b3cf0','#43e97b','#f093fb','#909399','#00bcd4','#ff9800']
    }
  },
  computed:{
    hasData:function(){ return this.rows.some(function(r){ return r.label && r.value != null }) },
    statsText:function(){
      var valid = this.rows.filter(function(r){ return r.value != null && !isNaN(r.value) })
      if (!valid.length) return ''
      var sum = valid.reduce(function(s, r){ return s + (r.value || 0) }, 0)
      return this.$t('tool.ChartMaker.stats', { n: valid.length, total: sum.toLocaleString() })
    }
  },
  created:function(){ this.loadSample() },
  mounted:function(){ this.loadEcharts(); this.renderChart() },
  beforeDestroy:function(){ if (this.chart) this.chart.dispose() },
  methods:{
    loadEcharts:function(){
      if (window.echarts) return
      if (echartsLoaded) { var s = this; setTimeout(function(){ s.loadEcharts() }, 100); return }
      echartsLoaded = true
      var script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js'
      script.onload = this.renderChart
      document.head.appendChild(script)
    },
    renderChart:function(){
      var s = this
      this.$nextTick(function(){
        if (!window.echarts || !s.$refs.chart) return
        if (!s.chart) s.chart = window.echarts.init(s.$refs.chart)
        var labels = s.rows.map(function(r){ return r.label || '' })
        var values = s.rows.map(function(r){ return r.value || 0 })
        var opt = {
          title: s.title ? { text:s.title, left:'center', textStyle:{ fontSize:16, color:'#303133' } } : undefined,
          tooltip: { trigger: s.chartType === 'pie' ? 'item' : 'axis' },
          grid: s.chartType !== 'pie' ? { left:55, right:30, top: s.title ? 45 : 15, bottom:25 } : undefined,
          xAxis: s.chartType === 'pie' ? undefined : s.chartType === 'hbar' ? { type:'value' } : { type:'category', data:labels, axisLabel:{ fontSize:12 } },
          yAxis: s.chartType === 'pie' ? undefined : s.chartType === 'hbar' ? { type:'category', data:labels, axisLabel:{ fontSize:12 } } : { type:'value' },
          series: [{
            type: s.chartType === 'hbar' ? 'bar' : s.chartType,
            data: s.chartType === 'pie' ? labels.map(function(l,i){ return { name:l, value:values[i] } }) : values,
            barWidth: Math.max(12, Math.min(32, Math.floor(400 / Math.max(1, labels.length)))),
            itemStyle: {
              borderRadius: s.chartType === 'hbar' ? [0,4,4,0] : [4,4,0,0],
              color: s.chartType === 'pie' ? undefined : s.colors[0]
            },
            color: s.colors,
            label: s.chartType === 'pie' ? { show:true, formatter:'{b}: {c}' } : undefined
          }]
        }
        s.chart.setOption(opt, true)
        s.chart.resize()
      })
    },
    addRow:function(){ this.rows.push({ label:'', value:0 }); this.renderChart() },
    removeRow:function(i){ if (this.rows.length > 1) { this.rows.splice(i, 1); this.renderChart() } },
    download:function(){
      if (!this.chart) return
      var a = document.createElement('a')
      document.body.appendChild(a)
      a.href = this.chart.getDataURL({ type:'png', pixelRatio:2, backgroundColor:'#fff' })
      a.download = 'chart.png'; a.click()
      document.body.removeChild(a)
    },
    loadSample:function(){
      var months = this.$t('tool.ChartMaker.sampleMonths')
      this.title = this.$t('tool.ChartMaker.sampleTitle')
      this.rows = [
        { label:months[0], value:820 }, { label:months[1], value:932 }, { label:months[2], value:901 },
        { label:months[3], value:934 }, { label:months[4], value:1290 }, { label:months[5], value:1330 }
      ]
      this.chartType = 'bar'
      this.$nextTick(this.renderChart)
    },
    doAction:function(a){
      if (a === 'sample') this.loadSample()
      if (a === 'clear') { this.rows = [{ label:'', value:0 }]; this.title = ''; this.chartType = 'bar'; this.$nextTick(this.renderChart) }
    }
  }
}
</script>

<style scoped>
.cm-tool { width: 100%; }

/* input */
.cm-input-section { margin-bottom: 16px; }
.cm-input-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #606266; margin-bottom: 12px; }
.cm-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: #4e7cff; box-shadow: 0 0 6px rgba(78,124,255,.4); }
.cm-toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

/* table */
.cm-table { border-radius: 10px; overflow: hidden; border: 1px solid #ebeef5; margin-bottom: 16px; }
.cm-row { display: flex; border-bottom: 1px solid #f0f2f5; align-items: center; }
.cm-row:last-child { border-bottom: none; }
.cm-head { background: #f8f9fb; font-size: 12px; color: #8892a6; font-weight: 600; }
.cm-cell { padding: 8px 10px; border: none; outline: none; font-size: 13px; color: #303133; background: transparent; display: flex; align-items: center; border-right: 1px solid #f0f2f5; }
.cm-cell:last-child { border-right: none; }
.cm-cell-swatch { flex: 0 0 46px; justify-content: center; }
.cm-cell-label { flex: 2; }
.cm-cell-val { flex: 1; }
.cm-cell-del { flex: 0 0 36px; justify-content: center; cursor: pointer; color: #c0c4cc; font-size: 18px; }
.cm-cell-del:hover { color: #f56c6c; }
input.cm-cell { font-family: inherit; min-width: 0; box-sizing: border-box; }
input.cm-cell:focus { background: #f0f4ff; }
.cm-swatch { width: 14px; height: 14px; border-radius: 3px; flex-shrink: 0; }

/* summary */
.cm-summary { background: #fafbfc; }
.cm-sum-text { padding: 8px 16px; font-size: 12px; color: #909399; }

/* chart */
.cm-chart { background: #fff; border-radius: 10px; border: 1px solid #ebeef5; padding: 10px; margin-bottom: 10px; width: 100%; height: 360px; }
.cm-actions { text-align: center; margin-bottom: 8px; }

@media (max-width: 640px) {
  .cm-toolbar { flex-direction: column; align-items: stretch; }
  .cm-chart { height: 280px; }
}
</style>
